import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminAuth } from "@/lib/AdminAuth";
import { formatApiErrorDetail } from "@/lib/adminApi";

export default function AdminLogin() {
  const { user, login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Admin sign in · RentAnything.ai";
  }, []);

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.trim() || !password) {
      setErr("Email and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Signed in");
      navigate("/admin", { replace: true });
    } catch (e2) {
      const msg = formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
      setErr(msg || "Couldn't sign you in.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen ra-mesh flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <Logo to={null} height={28} />
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-ra-ocean">Internal Admin</p>
        </div>
        <form
          onSubmit={onSubmit}
          className="rounded-3xl border border-ra-line bg-white p-7 sm:p-9 shadow-card"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ra-ocean/15 text-ra-ocean">
              <Lock size={16} />
            </span>
            <div>
              <h1 className="font-display text-xl font-semibold text-ra-ink leading-tight">Sign in</h1>
              <p className="text-xs text-ra-ink/60">RentAnything.ai admin CRM</p>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@rentanything.ai"
                className="mt-1.5"
                data-testid="admin-login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                data-testid="admin-login-password"
              />
            </div>

            {err && (
              <p className="text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="admin-login-error">
                {err}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="ra-btn-primary w-full"
              data-testid="admin-login-submit"
            >
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Signing in</> : <>Sign in <ArrowRight size={16} /></>}
            </button>
          </div>

          <p className="mt-6 text-center text-[11px] text-ra-ink/50">
            Forgot your password? Ping the team — admin reset is manual in Phase 2.
          </p>
        </form>
      </div>
    </div>
  );
}
